import { ChangeDetectorRef, Component, Inject, InjectionToken, ViewChild, importProvidersFrom, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatCardModule } from '@angular/material/card';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';

import { ToolBarComponent } from './components/navigation/tool-bar/tool-bar.component';
import { LeftSidenavComponent } from "./components/navigation/left-sidenav/left-sidenav.component";
import { RightSidenavComponent } from "./components/navigation/right-sidenav/right-sidenav.component";
import { NavigationComponent } from "./components/navigation/navigation/navigation.component";
import { AppraisalTableComponent } from "./components/appraisal-table/appraisal-table.component";
import { AuthenticationResult, InteractionStatus, PopupRequest, RedirectRequest } from '@azure/msal-browser';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalModule, MsalService } from '@azure/msal-angular';
import { HttpClientModule } from '@angular/common/http';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { Subject, filter, takeUntil } from 'rxjs';

import { MediaMatcher } from '@angular/cdk/layout';
import { NavDataService } from './shared/services/nav-data.service';
import { NavItem } from './models/nav-item';
import { AppraisalDataService } from './shared/services/appraisal-data.service';
import { environment } from '../environments/environment';

import { ArgusComponent } from './components/argus/argus.component';

import { NgModule, LOCALE_ID } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { DatePipe } from '@angular/common';

// Import the locale data
import localeEn from '@angular/common/locales/en';
import { registerLocaleData } from '@angular/common';
import { ChangeHistoryComponent } from './components/change-history/change-history.component';
import { SectionService } from './shared/services/section.service';
import { UserService } from './shared/services/user.service';

@Component({
  selector: 'app-root',
  standalone: true,
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  imports: [CommonModule, HttpClientModule, FormsModule, MsalModule, MatMenuModule, MatButtonModule, MatCardModule, MatToolbarModule, ToolBarComponent, LeftSidenavComponent, RightSidenavComponent,
    NavigationComponent, AppraisalTableComponent,ChangeHistoryComponent,
    RouterOutlet, RouterLink, MatSidenavModule, MatListModule, MatIconModule, ArgusComponent],
  providers: []
})
export class AppComponent {
  mobileQuery: MediaQueryList;
  private _mobileQueryListener: () => void;
  @ViewChild(LeftSidenavComponent) sidenavComponent!: LeftSidenavComponent;
  @ViewChild(RightSidenavComponent) rightSidenavComponent!: RightSidenavComponent;

  isIframe = false;
  loginDisplay = false;
  private readonly _destroying$ = new Subject<void>();
  public navData: NavItem[] = [];
  selectedRecord: any = null;
  selectedRecordSections: string[] = [];
  selectedSubsection: string = 'Subject Property';
  isDynamicSectionExpanded = true;

  selectedComponent: any = null;
  selectedArgusData = {
    propertyName: 'Empire State Building',
    location: 'New York, USA',
    valuation: '$2.1 Billion'
  };
  collapsed = JSON.parse(localStorage.getItem('leftNavCollapsed') ?? 'false');

  toggleCollapse(): void {
    this.collapsed = !this.collapsed;
    localStorage.setItem('leftNavCollapsed', JSON.stringify(this.collapsed));
  }

  trackByTitle = (_: number, it: any) => it?.title ?? _;
  userData: any;
  constructor(@Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    changeDetectorRef: ChangeDetectorRef, media: MediaMatcher,
    private navDataService: NavDataService,
    private appraisalDataService: AppraisalDataService, private sectionService: SectionService,
    public router: Router,
    private userService: UserService) {
    this.mobileQuery = media.matchMedia('(max-width: 600px)');
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
    this.mobileQuery.addListener(this._mobileQueryListener);
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.appraisalDataService.rowData$.subscribe(record => {
      if (record) {
        this.selectedRecord = record;
        this.selectedRecordSections = ['Subject Property'];
        this.selectedSubsection = localStorage.getItem('selectedSubsection') || 'Subject Property';
        this.isDynamicSectionExpanded = true;
        try {
          localStorage.setItem('selectedRecord', JSON.stringify(record));
        } catch (error) {
          console.warn('Unable to cache selected record', error);
        }
      } else {
        this.selectedRecord = null;
        this.selectedRecordSections = [];
      }
    });
    this.sectionService.selectedSubSection$.subscribe(record => {
      this.selectedSubsection = record;
    });
  }

  ngOnInit() {
    // console.log('Environment:', environment);
    this.authService.handleRedirectObservable().subscribe();
    this.isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal

    this.setLoginDisplay();

    this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      });

    const currentUrl = this.router.url;
    const storedSection = localStorage.getItem('selectedSubsection');

    if (currentUrl.includes('activity-details')) {
      if (storedSection) {
        this.selectedSubsection = storedSection;
      } else if (currentUrl.includes('/activity-details/argus')) {
        this.selectedSubsection = 'Argus';
        localStorage.setItem('selectedSubsection', 'Argus');
      }
      else if (currentUrl.includes('/activity-details/comparables')) {
        this.selectedSubsection = 'Comparables';
        localStorage.setItem('selectedSubsection', 'Comparables');
      }

      this.restoreCachedActivityContext();
      this.isDynamicSectionExpanded = true;
    }

    this.userService.userData$.subscribe((data) => {
      this.userData = data;
      if (this.userData != null || this.userData != undefined) {
        let navData: any;
        this.navDataService.getNavData().subscribe(data => {
          navData = data;
        });
        const hasRatingsRole = this.userData.roles?.includes('Ratings') === true;

        this.navData = navData.filter((x: NavItem) =>
          hasRatingsRole ? x.title === 'Ratings' : x.title !== 'Ratings'
        );
      }
    });
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  loginRedirect() {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
    } else {
      this.authService.loginRedirect();
    }
  }

  loginPopup() {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginPopup({ ...this.msalGuardConfig.authRequest } as PopupRequest)
        .subscribe((response: AuthenticationResult) => {
          this.authService.instance.setActiveAccount(response.account);
        });
    } else {
      this.authService.loginPopup()
        .subscribe((response: AuthenticationResult) => {
          this.authService.instance.setActiveAccount(response.account);
        });
    }
  }

  logout(popup?: boolean) {
    localStorage.removeItem('selectedSubsection');
    localStorage.removeItem('selectedRecord');
    if (popup) {
      this.authService.logoutPopup({
        mainWindowRedirectUri: "/"
      });
    } else {
      this.authService.logoutRedirect();
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  toggleSidenav() {
    this.toggle();
  }

  @ViewChild('leftSideNav') drawer!: MatSidenav;  // Non-null assertion operator

  toggle(): void {
    this.drawer.toggle();
  }

  toggleRightSidenav() {
    this.sidenavComponent.toggleRightSidenav();
  }

  // 1) Add an object to track expanded states
  expandedStates: { [key: string]: boolean } = {};

  // 2) Generate a unique key for each item+level combination
  getItemKey(item: any, level: number): string {
    return `${item.title}-${level}`;
  }

  // 3) Toggle the expanded state in this method
  toggleItem(item: any, level: number): void {
    const key = this.getItemKey(item, level);
    this.expandedStates[key] = !this.expandedStates[key];
  }

  // 4) Return the expanded state for the item; if undefined, it's false by default
  isExpanded(item: any, level: number): boolean {
    const key = this.getItemKey(item, level);
    return !!this.expandedStates[key];
  }


  toggleDynamicSection() {
    this.isDynamicSectionExpanded = !this.isDynamicSectionExpanded;
  }

  clearSelectedRecord() {
    this.selectedRecord = null;
    this.selectedRecordSections = [];
    localStorage.removeItem('selectedRecord');
    localStorage.removeItem('selectedSubsection');
  }

  loadArgusComponent() {
    let activityNumber = this.selectedRecord.activityNumber;
    this.router.navigate([`/activity-details/argus/${activityNumber}`]);
  }  

  loadComparables() {
    let activityNumber = this.selectedRecord.activityNumber;
    this.router.navigate([`/activity-details/comparables/${activityNumber}`]);
  }  

  // Method to handle selection of a subsection.
  selectSection(section: string): void {
    this.selectedSubsection = section;
    const activityNumber = this.selectedRecord?.activityNumber;

    if (section === 'Argus') {
      this.router.navigate([`/activity-details/argus/${activityNumber}`]);
    } else if(section === 'ChangeHistory') {
      this.router.navigate([`/activity-details/changeHistory/${activityNumber}`]);
    } else if(section === 'Comparables') {
      this.router.navigate([`/activity-details/comparables/${activityNumber}`]);
    }else {
      this.router.navigate([`/activity-details/${activityNumber}`]);
    }

    localStorage.setItem('selectedSubsection', section);
  }

  private restoreCachedActivityContext(): void {
    const storedRecord = localStorage.getItem('selectedRecord');

    if (storedRecord && !this.selectedRecord) {
      try {
        this.selectedRecord = JSON.parse(storedRecord);
        this.selectedRecordSections = ['Subject Property'];
        this.isDynamicSectionExpanded = true;
      } catch (error) {
        localStorage.removeItem('selectedRecord');
        console.warn('Unable to restore cached activity record', error);
      }
    }
  }

  navigateToActivity(): void {
    // Navigate to the route "hostname/activity".
    // Adjust the route array as needed.
    const activityNumber = this.selectedRecord.activityNumber;
    this.router.navigate([`/activity-details/${activityNumber}`]);
  }
}

